import pathUtility from './path.utility.js'
import ticketSettingsHostUtility from './ticketSettingsHost.utility.js'

import { HOST } from '../config/scope.config.js'

function buildScopeKey(scope) {
  return `${scope.kind}:${[...new Set(scope.accounts)].sort().join(',')}`
}

function sortAccountIds(ids) {
  return [...new Set((ids ?? []).map(String).filter(Boolean))].sort()
}

function clampScopeActive(active, accounts) {
  const allowed = new Set((accounts ?? []).map(String))
  return [...new Set((active ?? []).map(String).filter((id) => allowed.has(id)))].sort()
}

function resolveScopeIds(scope) {
  if (scope.active.length) return sortAccountIds(scope.active)
  if (scope.accounts.length) return []
  return sortAccountIds(scope.accounts)
}

function isFullUniverse(active, accounts) {
  const universe = sortAccountIds(accounts)
  if (!universe.length) return true
  const picked = sortAccountIds(active)
  if (picked.length !== universe.length) return false
  return picked.every((id, index) => id === universe[index])
}

function sameScope(a, b) {
  const kind = a.kind === b.kind
  const accounts = a.accounts.length === b.accounts.length && b.accounts.every((id) => new Set(a.accounts).has(id))
  const active = a.active.length === b.active.length && b.active.every((id) => new Set(a.active).has(id))
  return kind && accounts && active
}

function accountIdsForGroup(group, fallbackAccountId) {
  const accounts = ticketSettingsHostUtility.accountIdsInGroup(group)
  if (accounts.length) return [...new Set(accounts)].sort()
  return fallbackAccountId ? [String(fallbackAccountId)] : []
}

function ticketAccountHost(user) {
  const accountId = String(user.currentAccount._id)
  return {
    ...HOST,
    merged: true,
    onModel: 'TicketAccount',
    hostId: accountId,
    scopeKey: ticketSettingsHostUtility.ticketAccountKey(accountId),
    ids: [accountId],
    apiScope: pathUtility.scope({ type: 'account' }),
  }
}

function ticketGroupHost(user, groupId, group) {
  const id = String(groupId)
  return {
    ...HOST,
    merged: true,
    onModel: 'TicketGroup',
    hostId: id,
    scopeKey: ticketSettingsHostUtility.ticketGroupKey(id),
    apiScope: pathUtility.scope({ type: 'group', groupId: id }),
    ticketGroupId: id,
    ids: accountIdsForGroup(group, user.currentAccount._id),
  }
}

function resolveHost({ scope, user, ticketGroupId = null }) {
  if (!user) return { ...HOST, ids: [] }

  if (ticketGroupId) {
    const group = ticketSettingsHostUtility.findGroup(user, ticketGroupId)
    if (!group) return { ...HOST, ids: [] }
    return ticketGroupHost(user, ticketGroupId, group)
  }

  if (scope.kind === 'account') {
    const accountId = String(user.currentAccount._id)
    const group = ticketSettingsHostUtility.findGroupForAccount(user, accountId)
    if (group?._id) return ticketGroupHost(user, group._id, group)
    return ticketAccountHost(user)
  }

  return { ...HOST, ids: [] }
}

class ScopeUtility {
  /**
   * Serialize a scope to a stable identity string — `kind` plus the sorted
   * account universe. Excludes `active`.
   *
   * @param {{ kind: string|null, accounts: string[], active: string[] }} scope
   * @returns {string}
   */
  key(scope) {
    return buildScopeKey(scope)
  }

  /**
   * Dedupe and sort account ids.
   *
   * @param {string[]} ids
   * @returns {string[]}
   */
  get(ids) {
    return sortAccountIds(ids)
  }

  /**
   * Restrict `active` to ids present in `accounts`. Returns `[]` when nothing
   * matches.
   *
   * @param {string[]} active
   * @param {string[]} accounts
   * @returns {string[]}
   */
  clamp(active, accounts) {
    return clampScopeActive(active, accounts)
  }

  /**
   * Resolved account ids for list queries — `active` when non-empty, otherwise
   * the full `accounts` universe.
   *
   * @param {{ kind: string|null, accounts: string[], active: string[] }} scope
   * @returns {string[]}
   */
  ids(scope) {
    return resolveScopeIds(scope)
  }

  /**
   * Whether `active` covers the full `accounts` universe (same members, order
   * ignored). Empty `accounts` is treated as full.
   *
   * @param {string[]} active
   * @param {string[]} accounts
   * @returns {boolean}
   */
  isFullUniverse(active, accounts) {
    return isFullUniverse(active, accounts)
  }

  /**
   * Whether two scopes are equal — same `kind`, account universe, and
   * `active` filter (order-independent).
   *
   * @param {{ kind: string|null, accounts: string[], active: string[] }} a
   * @param {{ kind: string|null, accounts: string[], active: string[] }} b
   * @returns {boolean}
   */
  same(a, b) {
    return sameScope(a, b)
  }

  resolve = {
    /**
     * Account ids for generation checks on a merged settings host.
     *
     * @param {{ scope: { kind: string|null, accounts: string[], active: string[] }, user: object|null|undefined, ticketGroupId?: string|null }} context
     * @returns {string[]}
     */
    ids(context) {
      return resolveHost(context).ids
    },

    /**
     * Settings-host cache key — `TicketAccount:<id>` or `TicketGroup:<id>`,
     * matching the server `settingsHostKey` shape. Returns `null` when the page
     * has no merged settings host.
     *
     * @param {{ scope: { kind: string|null, accounts: string[], active: string[] }, user: object|null|undefined, ticketGroupId?: string|null }} context
     * @returns {string|null}
     */
    key(context) {
      return resolveHost(context).scopeKey
    },

    /**
     * Resolve the merged ticket-settings host for load, save, and cache `meta`.
     *
     * Merged hosts:
     * - `account` scope — current account's `TicketAccount`, or its linked
     *   `TicketGroup` when `currentAccount.ticketGroup` is set.
     * - Any scope with an explicit `ticketGroupId` (route/query picker).
     *
     * Non-merged (`merged: false`) — `tickets` / `group` / `admin` without an
     * explicit ticket-group selection; list rows carry per-row hosts instead.
     *
     * @param {{ scope: { kind: string|null, accounts: string[], active: string[] }, user: object|null|undefined, ticketGroupId?: string|null }} context
     * @returns {{
     *   merged: boolean,
     *   onModel: 'TicketAccount' | 'TicketGroup' | null,
     *   hostId: string | null,
     *   scopeKey: string | null,
     *   ids: string[],
     *   apiScope: string | null,
     *   ticketGroupId: string | null,
     * }}
     */
    host(context) {
      return resolveHost(context)
    },
  }
}

export default new ScopeUtility()
